import React from 'react'
import styled from '@emotion/styled'
import axios from 'axios'
import isEmpty from 'validator/lib/isEmpty'
import isEmail from 'validator/lib/isEmail'
import ThemeConsumer from '@/hoc/ThemeConsumer'
import WithStylesProps from '@/hoc/WithStylesProps'
import { tabletS, mobile } from '@/utils/media'
import { mq } from '@/utils/helpers'
import { getThemeColor } from '@/utils/themes'
import LoadingMask from '@/components/common/LoadingMask'
import MessageDialog from '@/components/common/MessageDialog'
import ErrorDialog from '@/components/common/ErrorDialog'
import FormControl from '@material-ui/core/FormControl'
import TextField from '@/components/common/TextField'
import Select from '@/components/common/Select'
import InputLabel from '@/components/common/InputLabel'
import * as MenuItem from '@/components/common/MenuItem'
import FormControlLabel from '@/components/common/FormControlLabel'
import Checkbox from '@/components/common/Checkbox'
import FormHelperText from '@/components/common/FormHelperText'
import Typography from '@/components/common/Typography'
import GhostButton from '@/components/common/GhostButton'

class Form extends React.Component {
  constructor (props) {
    super(props)
    const state = {
      submitStatus: 0, // 0: idle, 1: sending, 2: error, 3: complete
      values: {
        agree: null,
        recaptcha: null
      },
      error: {}
    }
    this.props.inputFields.forEach(field => {
      state.values[field.name] = field.value || ''
      state.error[field.name] = null
    })
    this.state = state
  }
  componentDidMount () {
    this.waitUntilRecaptchaLoaded()
  }
  waitUntilRecaptchaLoaded () {
    if (typeof window === 'undefined') {
      return
    }
    window.requestAnimationFrame(() => {
      if (!window.grecaptcha) {
        this.waitUntilRecaptchaLoaded()
        return
      }
      this.onRecaptchaLoaded()
    })
  }
  onRecaptchaLoaded () {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute('6LfHVJYpAAAAAD4qKefUc-SBf6u2QL3vNYd2aK1o', { action: 'submit' }).then((token) => {
        this.setState({
          values: {
            ...this.state.values,
            recaptcha: token
          }
        })
      })
    })
  }
  handleChange = (event) => {
    this.setState({
      values: {
        ...this.state.values,
        [event.target.name]: event.target.value
      }
    })
  }
  handleCheckboxChange = (event) => {
    this.setState({
      values: {
        ...this.state.values,
        [event.target.name]: event.target.checked ? event.target.value : null
      }
    })
  }
  onMessageModalClose = () => { this.setState({ submitStatus: 0 }) }
  onErrorModalClose = () => { this.setState({ submitStatus: 0 }) }
  onErrorModalRetry = () => { this.submit() }
  submit = async () => {
    if (!this.validate()) {
      return
    }
    this.setState({ submitStatus: 1, error: {} })
    const res = await axios.post(this.props.action, this.state.values).catch(() => {
      return 'error'
    })
    setTimeout(() => {
      if (res === 'error') {
        this.setState({
          submitStatus: 2
        })
        return
      }
      this.setState({
        submitStatus: 3
      })
      window.dataLayer.push({
        event: 'app.form',
        eventCategory: this.props.gtm.category,
        eventAction: this.props.gtm.action,
        eventLabel: this.props.gtm.label
      })
    }, 1000)
  }
  validate () {
    const state = { ...this.state, error: {} }
    let result = true
    this.props.inputFields.forEach(field => {
      result = this.validateRequired(field, state) && result
    })
    if (!result) {
      this.setState(state)
    }
    return result
  }
  validateRequired (field, state) {
    if (field.required && isEmpty(state.values[field.name])) {
      state.error[field.name] = field.type === 'select'
        ? `${field.label}を選択してください。`
        : `${field.label}を入力してください。`
      return false
    }
    return true
  }
  validateEmail (field, state) {
    if (field.type === 'email' && isEmail(state.values[field.name])) {
      state.error[field.name] = 'メールアドレスを入力してください。'
      return false
    }
    return true
  }
  render () {
    const { theme, scale, type, inputFields, classes, privacy, ...other } = this.props
    return (
      <Root theme={theme} scale={scale} type={type} {...other}>
        <LoadingMask open={this.state.submitStatus === 1} message="送信中..." />
        <MessageDialog title="送信完了" open={this.state.submitStatus === 3} onClose={this.onMessageModalClose}>
          このたびはお問合せいただき、誠にありがとうございました。お送りいただいた内容を確認の上、担当者より折り返しご連絡させていただきます。<br />
          数日経っても連絡が来ない場合は、入力いただいたメールアドレスに誤りがあった可能性がございます。メールアドレスをご確認の上、もう一度フォームよりお問合せ頂きますようお願い申し上げます。
        </MessageDialog>
        <ErrorDialog title="送信エラー" open={this.state.submitStatus === 2} onClose={this.onErrorModalClose} onRetry={this.onErrorModalRetry}>
          フォームを送信できませんでした。ネットワーク状況をご確認の上、再度送信をお願いいたします。
        </ErrorDialog>
        <Container>
          <Privacy theme={theme} scale={scale}>
            <Privacy_Title variant="h4" component="h3" theme={theme}>{privacy.title}</Privacy_Title>
            <Privacy_Desc variant="body2" theme={theme} rootScale={scale} rootType={type} dangerouslySetInnerHTML={{ __html: privacy.body }}></Privacy_Desc>
            <Privacy_Check>
              <FormControlLabel
                control={
                  <Checkbox
                    name="agree"
                    value="checked"
                    checked={this.state.values.agree === 'checked'}
                    onChange={this.handleCheckboxChange}
                  />
                }
                label="同意する"
              />
            </Privacy_Check>
          </Privacy>
          <Fields>
            {inputFields.map((field, index) => {
              let Component
              switch (field.type) {
              case 'select':
                Component = this.renderSelect(field, theme, classes)
                break
              case 'text':
              case 'number':
              case 'email':
                Component = this.renderTextField(field, theme, classes)
                break
              case 'textarea':
                Component = this.renderTextArea(field, theme, classes)
                break
              default:
                break
              }
              return Component
            })}
          </Fields>
          <Submit>
            <Button disabled={this.state.values.agree !== 'checked'} onClick={this.submit}>送信する</Button>
          </Submit>
          <Recaptcha theme={theme}>
            <p>このサイトは reCAPTCHA <br/>で保護されています。</p>
            <p><a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer noopener">プライバシー</a>・<a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer noopener">利用規約</a></p>
          </Recaptcha>
        </Container>
      </Root>
    )
  }
  renderSelect (field, theme, classes) {
    return (
      <Control key={field.name} required={field.required} error={!!this.state.error[field.name]}>
        <InputLabel disabled={this.state.values.agree !== 'checked'}>{field.label}</InputLabel>
        <Select
          value={this.state.values[field.name]}
          onChange={this.handleChange}
          inputProps={{
            name: field.name,
            id: field.name
          }}
          disabled={this.state.values.agree !== 'checked'}
        >
          <MenuItem.default value="" classes={{ ...MenuItem.get(classes) }}><Default>選択</Default></MenuItem.default>
          {field.values.map(({ label, value }, index) => (
            <MenuItem.default key={index} value={value} classes={{ ...MenuItem.get(classes) }}>{label}</MenuItem.default>
          ))}
        </Select>
        {this.state.error[field.name] && <FormHelperText>{this.state.error[field.name]}</FormHelperText>}
      </Control>
    )
  }
  renderTextField (field, theme, classes) {
    return (
      <Control key={field.name} error={!!this.state.error[field.name]}>
        <TextField
          required={field.required}
          error={!!this.state.error[field.name]}
          label={field.label}
          value={this.state.values[field.name]}
          onChange={this.handleChange}
          InputProps={{
            name: field.name,
            id: field.name
          }}
          disabled={this.state.values.agree !== 'checked'}
        />
        {this.state.error[field.name] && <FormHelperText>{this.state.error[field.name]}</FormHelperText>}
      </Control>
    )
  }
  renderTextArea (field, theme, classes) {
    return (
      <Control key={field.name} error={!!this.state.error[field.name]}>
        <TextField
          required={field.required}
          error={!!this.state.error[field.name]}
          label={field.label}
          value={this.state.values[field.name]}
          onChange={this.handleChange}
          multiline
          rowsMax="20"
          InputProps={{
            name: field.name,
            id: field.name
          }}
          disabled={this.state.values.agree !== 'checked'}
        />
        {this.state.error[field.name] && <FormHelperText>{this.state.error[field.name]}</FormHelperText>}
      </Control>
    )
  }
}

const getStyles = (props) => {
  if (props.type === 'contrast') {
    return {
      backgroundColor: props.theme.background.contrast,
      linkColor: props.theme.primary.base,
      borderColor: props.theme.scale[200]
    }
  } else if (props.scale) {
    return {
      backgroundColor: props.theme.scale[props.scale],
      linkColor: props.scale <= 500 ? props.theme.text.default : props.theme.primary.base,
      borderColor: props.theme.scale[200]
    }
  } else {
    return {
      backgroundColor: props.theme.background.default,
      linkColor: props.theme.primary.base,
      borderColor: props.theme.scale[200]
    }
  }
}

const Root = styled.form`
  padding: 80px;
  background-color: ${props => getStyles(props).backgroundColor};
  @media ${mq.and(tabletS)} {
    padding: 40px 24px;
  }
  @media ${mq.and(mobile)} {
    padding: 40px 16px;
  }
`

const Container = styled.div`
  margin: 0 auto;
  max-width: 624px;
  @media ${mq.and(tabletS, mobile)} {
    max-width: 100%;
  }
`

const Fields = styled.div`
  margin-top: 24px;
`

const Default = styled.em`
  font-weight: bold;
`

const Privacy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 24px 16px;
  border: 1px solid ${props => getStyles(props).borderColor};

  @media ${mq.and(mobile)} {
    padding: 16px;
  }
`

const Privacy_Title = styled(Typography)`
`

const Privacy_Desc = styled.div`
  margin: 24px auto 0;
  max-width: 624px;
  text-align: left;
  font-size: 14px;
  line-height: 1.75;

  @media ${mq.and(mobile)} {
    font-size: 13px
  }

  > * {
    margin: 16px 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1,h2,h3,h4,h5,h6 {
    font-weight: bold;
    font-size: 16px;
    line-height: 1.5;

    @media ${mq.and(mobile)} {
      font-size: 15px
    }
  }

  a {
    text-decoration: underline;
    font-weight: bold;
    color: ${props => getStyles({
      ...props,
      scale: props.rootScale,
      type: props.rootType
    }).linkColor};
  }
`

const Privacy_Check = styled.div`
  display: flex;
  justify-content: center;
`

const Submit = styled.div`
  margin-top: 64px;
  @media ${mq.and(mobile)} {
    margin-top: 32px;
  }
  text-align: center;
`

const Button = styled(GhostButton)`
  width: 180px;
`

const Control = styled(FormControl)`
  &:not(:first-of-type) {
    margin-top: 24px;
  }
  width: 100%;
`

const Recaptcha = styled.div`
  margin-top: 16px;
  text-align: center;
  
  p {
    font-size: 12px;
    line-height: 1.5;

    @media ${mq.and(mobile)} {
      font-size: 11px;
    }

    br {
      display: none;
    }

    @media ${mq.and(mobile)} {
      br {
        /* display: block; */
      }
    }
  }

  a {
    color: ${props => getThemeColor('primary.light', props.theme)};
    text-decoration: underline;
  }
`

const make = props => {
  return {
    ...MenuItem.make(props)
  }
}

export default ThemeConsumer(WithStylesProps(make)(Form))
